import {
  Injectable,
  inject,
  DestroyRef,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwPush } from '@angular/service-worker';
import { Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { Md5 } from 'ts-md5';

import { getClientTimezone, getBrowserCultureLang, isString } from '@core/helpers';

import { AuthState } from '../users/auth.state';

import { WebPushesStatus } from './web-pushes.entity';
import { WebPushesState } from './web-pushes.state';

type NotificationClick = {
  action: string;
  notification: NotificationOptions & {
    title: string;
  }
};

@Injectable({
  providedIn: 'root'
})
export class SwPushService {
  private readonly swPush = inject(SwPush, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly authState = inject(AuthState);
  private readonly webPushesState = inject(WebPushesState);

  public readonly status = signal<WebPushesStatus>(WebPushesStatus.UNSUBSCRIBED);

  constructor() {
    this.checkStatus();
    this.trackClicksAndViews();
  }

  get isSupported(): boolean {
    return !!this.swPush && this.swPush.isEnabled && 'Notification' in window;
  }

  get subscriptionId(): Observable<string|null> {
    if(!this.swPush || !this.isSupported || !this.swPush.subscription) {
      return of(null);
    }

    return this.swPush.subscription.pipe(
      take(1),
      map((pushSubscription: PushSubscription|null) => {
        if(!pushSubscription) {
          return null;
        }

        return Md5.hashStr(pushSubscription.endpoint);
      })
    );
  }

  async subscribe(publicKey: string): Promise<void> {
    let locale = getBrowserCultureLang();

    if(locale) {
      locale = locale.replace('-', '_');
    }

    const timezone = getClientTimezone();
    const permission = await Notification.requestPermission();

    if('granted' === permission && this.swPush) {
      try {
        const sub = await this.swPush.requestSubscription({ serverPublicKey: publicKey });
        const jsonResult = sub.toJSON();

        if(!jsonResult.keys || !jsonResult.endpoint) {
          this.saveError('Could not get keys or endpoint');

          return;
        }

        const data = {
          endpoint: jsonResult.endpoint,
          authKey: jsonResult.keys['auth'],
          publicKey: jsonResult.keys['p256dh'],
          expiresAt: jsonResult.expirationTime,
          encoding: (PushManager.supportedContentEncodings || ['aesgcm'])[0],
          vapidKey: publicKey,
          timezone,
          locale
        };

        this.webPushesState.subscribe(data)
          .pipe( takeUntilDestroyed(this.destroyRef) )
          .subscribe(() => this.setStatus(WebPushesStatus.SUBSCRIBED));
      } catch(err) {
        this.saveError(err);
        this.setStatus(WebPushesStatus.DENIED);
      }
    } else {
      this.setStatus(WebPushesStatus.DENIED);
    }
  }

  async unsubscribe() {
    if(!this.swPush) {
      throw Error('Pushes unsupported in this environment');
    }

    try {
      await this.swPush.unsubscribe();

      this.webPushesState.unsubscribe()
        .pipe( takeUntilDestroyed(this.destroyRef) )
        .subscribe(() => {
          this.setStatus(WebPushesStatus.SUPPORTED);
        });
    } catch(err) {
      this.saveError(err);

      throw err;
    }
  }

  private checkStatus(): void {
    this.subscriptionId
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe({
        next: (subscriptionId: string|null) => this.doCheckStatus(subscriptionId), 
        // eslint-disable-next-line no-console
        error: (err: unknown) => console.log(err)
      });
  }

  private doCheckStatus(subscriptionId: string|null): void {
    const subId = this.webPushesState.subId();

    if(subscriptionId !== subId && subId) {
      this.webPushesState.unsubscribe()
        .pipe( takeUntilDestroyed(this.destroyRef) )
        .subscribe();
    }

    if(this.isSupported) {
      if(Notification.permission === 'default' || (Notification.permission === 'granted' && !subscriptionId)) {
        this.setStatus(WebPushesStatus.SUPPORTED);
      } else if(Notification.permission === 'denied') {
        this.setStatus(WebPushesStatus.DENIED);
      } else if(subscriptionId && Notification.permission === 'granted') {
        this.setStatus(WebPushesStatus.SUBSCRIBED);

        const subscribedAsUser = this.webPushesState.subscribedAsUser();
        const isAuthenticated = this.authState.isAuthenticated();

        if(!subscribedAsUser && isAuthenticated) {
          this.webPushesState.linkSubscriptionWithUser(subscriptionId);
        }
      }
    } else {
      this.setStatus(WebPushesStatus.UNSUPPORTED);
    }
  }

  private trackClicksAndViews(): void {
    if(!this.isSupported) {
      return;
    }

    this.swPush!.messages
      .pipe(
        filter((data: any) => !!data.notification),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(notification => {
        const tag = Number(notification.tag);

        this.webPushesState.trackView(tag);
      });

    this.swPush!.notificationClicks
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe((data: NotificationClick) => {
        const { action, notification } = data;
        const tag = Number(notification.tag);

        this.webPushesState.trackClick(tag, action);
      });
  }

  private setStatus(status: WebPushesStatus): void {
    this.status.set(status);
  }

  private saveError(err: unknown): void {
    const errStr = isString(err) ? err : JSON.stringify(err);
    
    this.webPushesState.saveSubscriptionError(errStr);
  }
}